import { THrefLangItem, TSitecoreContext } from '../../../../types/types';
import { TLayoutSeoTagsProps, TRouteData } from '../types/layoutSeoTags.types';
import { windowService } from '../../../../utils/window.utils.js';

import { ISeoModel } from '../../../../types/seo.types';

export class LayoutSeoTagModel implements ISeoModel {
  private readonly context: TSitecoreContext;
  private readonly route: TRouteData;

  constructor(props: TLayoutSeoTagsProps) {
    this.context = props.sitecoreContext;
    this.route = props.route;
  }

  get title(): string {
    const pageTitle = this.route?.fields?.metaTitle?.value;
    const brandName = this.context?.metaData?.brandName;
    const pageTitleSuffix = this.context?.metaData?.browserTitleSuffix;
    const brandNameWithSuffix = brandName && pageTitleSuffix ? `${brandName} ${pageTitleSuffix}` : brandName;

    if (pageTitle && brandName) {
      return `${pageTitle} | ${brandNameWithSuffix}`;
    }
    return pageTitle || brandNameWithSuffix || '';
  }

  get description(): string {
    return this.route?.fields?.metaDescription?.value || '';
  }

  get keywords(): string {
    return this.route?.fields?.MetaKeywords?.value || '';
  }

  get robots(): string {
    return this.route?.fields?.RobotsContentNew?.value || this.route?.fields?.RobotsContent?.value || '';
  }

  get canonicalUrl(): string {
    const canonicalurl = this.context?.metaData?.fullCanonicalLink;
    if (canonicalurl && !canonicalurl.includes('/layout/render')) {
      return canonicalurl;
    }
    return windowService()?.location?.href;
  }

  get alternateLinks(): Array<THrefLangItem> {
    return this.context?.ContextExtensions?.hrefLanguageList || [];
  }

  get language(): string {
    return this.context?.language || '';
  }

  get ogTitle(): string {
    return this.title;
  }

  get ogSiteName(): string {
    return this.title;
  }

  get currentPageUrl(): string {
    return this.context?.currentPageUrl ?? '';
  }

  get ogUrl(): string {
    return this.route?.fields?.metaUrl?.value || this.currentPageUrl;
  }

  get ogImage(): string {
    return this.route?.fields?.metaImage?.value?.src || '';
  }

  get ogType(): string {
    return this.route?.fields?.metaType?.value || 'website';
  }

  get ogDescription(): string {
    return this.description;
  }

  get ogLocale(): string {
    return this.context?.language || '';
  }

  get ogLocaleAlternate(): string[] {
    return (
      this.context?.ContextExtensions?.hrefLanguageList
        ?.filter((langItem) => langItem?.langCode !== this.ogLocale)
        .map((langItem) => langItem.langCode || '') || []
    );
  }

  get twitterCard(): string {
    return 'summary';
  }

  get twitterSite(): string {
    return this.route?.fields.metaSite?.value || '';
  }

  get twitterTitle(): string {
    return this.route?.fields.metaTitle?.value || '';
  }

  get twitterDescription(): string {
    return this.description;
  }

  get twitterImage(): string {
    return this.route?.fields?.metaImage?.value?.src || '';
  }

  get gscVerificationCodes(): Array<string> {
    let values: Array<string> = [];
    const extMetaGSVC: Array<string> =
      typeof this.context?.metaData?.googleSiteVerificationCode === 'object'
        ? Object.values(this.context?.metaData?.googleSiteVerificationCode)
        : [];
    const regularGSVC: Array<string> = this.route?.fields?.googleSiteVerificationCode?.value?.map((link) => link.Value || '') || [];
    if (extMetaGSVC) {
      values = values.concat(extMetaGSVC);
    }
    if (regularGSVC) {
      values = values.concat(regularGSVC);
    }
    return values;
  }

  get favIconUrl(): string {
    return this.route?.fields?.Favicon?.value?.src || this.context?.metaData?.favicon?.src || '';
  }
}
